import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, projectIdParamsSchema } from '../schemas';
import {
	userNotificationSettingSchema,
	userNotificationSettingsSchema,
	baseNotificationSettingsSchema,
} from '../../interfaces/INotificationSettings';
import { NotificationTypes } from '../../interfaces/NotificationTypes.enum';

// ============================================================
// Shared schemas for settings
// ============================================================

// Dynamic enum schema — auto-includes any new NotificationTypes members
const notificationTypeSchema = z.enum(Object.values(NotificationTypes) as [NotificationTypes, ...NotificationTypes[]]);

// ============================================================
// Contract
// ============================================================

/**
 * `{ root: true }`: `/projects/:projectId` must precede the collection segment,
 * so every path in this file spells `/settings` itself.
 */
export const settingsContract = defineContract(
	'settings',
	{
		/**
		 * SELF scope. `username` was a query param with no ownership check, so any
		 * authenticated user could read — and through the sibling POST, rewrite —
		 * another user's notification settings in any project they both belong to.
		 * The server takes the caller from the token; the parameter is gone rather
		 * than validated, so aiming it at somebody else is not expressible.
		 *
		 * `/user` here names the CALLER, not an identity in the URI — which is why
		 * the segment stays while the username does not.
		 */
		getUserSettings: route({
			method: 'GET',
			path: '/projects/:projectId/settings/user',
			params: projectIdParamsSchema,
			response: userNotificationSettingsSchema,
			operationId: 'getUserNotificationSettings',
			tags: ['Settings'],
			summary: "Get the caller's notification settings for a project",
		}),
		getBaseSettings: route({
			method: 'GET',
			path: '/projects/:projectId/settings/base',
			params: projectIdParamsSchema,
			response: z.object({
				baseSettings: baseNotificationSettingsSchema,
			}),
			operationId: 'getBaseNotificationSettings',
			tags: ['Settings'],
			summary: 'Get base notification settings for a project',
		}),
		updateBaseSettings: route({
			method: 'POST',
			path: '/projects/:projectId/settings/base',
			params: projectIdParamsSchema,
			query: z.object({
				notificationType: notificationTypeSchema,
			}),
			body: z.object({
				setting: userNotificationSettingSchema.optional(),
				recipients: z
					.array(
						z.object({
							username: requiredStringSchema,
							add: z.boolean(),
						})
					)
					.optional(),
			}),
			response: z.string(),
			operationId: 'updateBaseNotificationSettings',
			tags: ['Settings'],
			summary: 'Update base notification settings',
		}),
		/** SELF scope — see `getUserSettings`. */
		updateUserSettings: route({
			method: 'POST',
			path: '/projects/:projectId/settings/user',
			params: projectIdParamsSchema,
			query: z.object({
				notificationType: notificationTypeSchema,
			}),
			body: z.object({
				setting: userNotificationSettingSchema,
			}),
			response: z.string(),
			operationId: 'updateUserNotificationSettings',
			tags: ['Settings'],
			summary: "Update the caller's notification settings",
		}),
	},
	{ root: true }
);
